import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import BreakingNews from '../components/BreakingNews';
import Footer from '../components/Footer';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Calendar, MapPin, X, ChevronLeft, ChevronRight, Image as ImageIcon } from 'lucide-react';
import { getImageUrl } from '../utils/imageHelper';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=800&h=800&fit=crop';

const PhotoArchives = () => {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const photoCategories = [
    { id: 'events', nameBengali: 'অনুষ্ঠান', nameEnglish: 'Events' },
    { id: 'culture', nameBengali: 'সংস্কৃতি', nameEnglish: 'Culture' },
    { id: 'politics', nameBengali: 'রাজনীতি', nameEnglish: 'Politics' },
    { id: 'sports', nameBengali: 'খেলাধুলা', nameEnglish: 'Sports' },
    { id: 'nature', nameBengali: 'প্রকৃতি', nameEnglish: 'Nature' },
    { id: 'heritage', nameBengali: 'ঐতিহ্য', nameEnglish: 'Heritage' },
  ];

  useEffect(() => {
    fetchGalleries();
  }, [selectedCategory]);

  const fetchGalleries = async () => {
    try {
      const url = selectedCategory === 'all' 
        ? `${API_URL}/api/galleries`
        : `${API_URL}/api/galleries?category=${selectedCategory}`;
      
      const response = await axios.get(url);
      setGalleries(response.data);
    } catch (error) {
      console.error('Failed to fetch galleries:', error);
    } finally {
      setLoading(false);
    }
  };

  const openPhotoModal = (photo, gallery, index) => {
    setSelectedPhoto(photo);
    setSelectedGallery(gallery);
    setCurrentPhotoIndex(index);
  };

  const closePhotoModal = () => {
    setSelectedPhoto(null);
    setSelectedGallery(null);
    setCurrentPhotoIndex(0);
  };

  const navigatePhoto = (direction) => {
    if (!selectedGallery) return;
    
    const newIndex = direction === 'next' 
      ? (currentPhotoIndex + 1) % selectedGallery.photos.length
      : (currentPhotoIndex - 1 + selectedGallery.photos.length) % selectedGallery.photos.length;
    
    setCurrentPhotoIndex(newIndex);
    setSelectedPhoto(selectedGallery.photos[newIndex]);
  };

  const filteredGalleries = galleries;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <BreakingNews />

      <div className="container mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            ছবির সংগ্রহশালা
          </h1>
          <p className="text-xl text-gray-600">Photo Archives</p>
        </div>

        {/* Category Filter */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => setSelectedCategory('all')}
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              className={selectedCategory === 'all' ? 'bg-red-600 hover:bg-red-700 text-white' : 'border-gray-300 hover:border-red-600'}
            >
              All / সব
            </Button>
            {photoCategories.map((category) => (
              <Button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                variant={selectedCategory === category.id ? 'default' : 'outline'}
                className={selectedCategory === category.id ? 'bg-red-600 hover:bg-red-700 text-white' : 'border-gray-300 hover:border-red-600'}
              >
                {category.nameEnglish} / {category.nameBengali}
              </Button>
            ))}
          </div>
        </div>

        {/* Photo Galleries */}
        <div className="space-y-12">
          {filteredGalleries.map((gallery) => (
            <div key={gallery.id} className="bg-white rounded-lg shadow-lg p-6">
              {/* Gallery Header */}
              <div className="mb-6 pb-4 border-b border-gray-200">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      {gallery.title}
                    </h2>
                    <p className="text-lg text-gray-600 mb-3">
                      {gallery.titleEnglish}
                    </p>
                  </div>
                  <Badge className="bg-red-600 text-white px-3 py-1">
                    {gallery.categoryBengali}
                  </Badge>
                </div>
                <p className="text-gray-700 mb-2">{gallery.description}</p>
                <p className="text-gray-600 mb-3">{gallery.descriptionEnglish}</p>
                <div className="flex items-center space-x-4 text-sm text-gray-500">
                  <div className="flex items-center space-x-1">
                    <Calendar className="w-4 h-4" />
                    <span>{gallery.date}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <MapPin className="w-4 h-4" />
                    <span>{gallery.location}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <ImageIcon className="w-4 h-4" />
                    <span>{gallery.photos ? gallery.photos.length : 0} photos</span>
                  </div>
                </div>
              </div>

              {/* Photo Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {gallery.photos && gallery.photos.length > 0 ? (
                  gallery.photos.map((photo, index) => (
                    <div
                      key={photo.id}
                      onClick={() => openPhotoModal(photo, gallery, index)}
                      className="group relative aspect-square overflow-hidden rounded-lg cursor-pointer shadow hover:shadow-xl transition-all duration-300"
                    >
                      <img
                        src={getImageUrl(photo.url)}
                        alt={photo.captionEnglish}
                        className="w-full h-full object-contain transform group-hover:scale-110 transition-transform duration-500"
                        onError={(e) => { e.target.src = FALLBACK_IMAGE; }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="absolute bottom-0 left-0 right-0 p-3">
                          <p className="text-white text-sm font-medium line-clamp-2">
                            {photo.caption}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-span-full text-center py-8 text-gray-500">
                    No photos in this gallery
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredGalleries.length === 0 && (
          <div className="text-center py-16">
            <ImageIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              No galleries found
            </h3>
            <p className="text-gray-500">Try selecting a different category</p>
          </div>
        )}
      </div>

      {/* Photo Modal/Lightbox */}
      {selectedPhoto && selectedGallery && (
        <Dialog open={!!selectedPhoto} onOpenChange={closePhotoModal}>
          <DialogContent className="max-w-6xl w-full p-0 bg-black border-none">
            <div className="relative">
              {/* Close Button */}
              <button
                onClick={closePhotoModal}
                className="absolute top-4 right-4 z-10 bg-white/10 hover:bg-white/20 text-white p-2 rounded-full transition-colors duration-200"
              >
                <X className="w-6 h-6" />
              </button>

              {/* Navigation Buttons */}
              {selectedGallery.photos.length > 1 && (
                <>
                  <button
                    onClick={() => navigatePhoto('prev')}
                    className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-colors duration-200"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={() => navigatePhoto('next')}
                    className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-colors duration-200"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </>
              )}

              {/* Photo */}
              <div className="flex items-center justify-center min-h-[60vh] max-h-[85vh]">
                <img
                  src={getImageUrl(selectedPhoto.url)}
                  alt={selectedPhoto.captionEnglish}
                  className="max-w-full max-h-[85vh] object-contain"
                  onError={(e) => { e.target.src = FALLBACK_IMAGE; }}
                />
              </div>

              {/* Photo Info */}
              <div className="bg-gray-900 text-white p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-lg font-semibold">{selectedPhoto.caption}</p>
                  <span className="text-sm text-gray-400">
                    {currentPhotoIndex + 1} / {selectedGallery.photos.length}
                  </span>
                </div>
                <p className="text-gray-300">{selectedPhoto.captionEnglish}</p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <Footer />
    </div>
  );
};

export default PhotoArchives;